import React from 'react';

const BRAND_STORAGE_KEY = 'feria_studio_brand_assets';

function storedBrandAssets() {
  if (typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(BRAND_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

export function studioInitials(name = 'Feria OS') {
  const parts = String(name || 'Feria OS').trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : (parts[0] || 'F').slice(0, 2)).toUpperCase();
}

export function resolveStudioBrand({
  data,
  studioLogo,
  studioBrandAssets,
  theme = 'light',
  surfaceTone,
} = {}) {
  const studio = data?.studio || {};
  const hasProvidedAssets = studioBrandAssets && Object.keys(studioBrandAssets).length > 0;
  const assets = hasProvidedAssets ? studioBrandAssets : storedBrandAssets();
  const studioName = assets.displayName || studio.display_name || studio.nombre_visible || studio.name || studio.nombre || 'Feria OS';
  const resolvedSurfaceTone = surfaceTone || (theme === 'dark' ? 'dark' : 'light');

  const fullLight = assets.fullLight
    || studio.logo_light_url
    || studio.logo_full_light_url
    || studio.full_logo_light_url
    || studio.logo_full_url
    || studio.logo_url
    || studioLogo
    || '';
  const fullDark = assets.fullDark
    || studio.logo_dark_url
    || studio.logo_full_dark_url
    || studio.full_logo_dark_url
    || studio.logo_full_url
    || studio.logo_url
    || studioLogo
    || fullLight;
  const isoLight = assets.isoLight
    || studio.isotype_light_url
    || studio.isotipo_light_url
    || studio.logo_compact_light_url
    || studio.isotype_url
    || studio.isotipo_url
    || studio.logo_compact_url
    || '';
  const isoDark = assets.isoDark
    || studio.isotype_dark_url
    || studio.isotipo_dark_url
    || studio.logo_compact_dark_url
    || studio.isotype_url
    || studio.isotipo_url
    || studio.logo_compact_url
    || isoLight;

  const fullLogoForLightSurface = fullDark || fullLight;
  const fullLogoForDarkSurface = fullLight || fullDark;
  const isoLogoForLightSurface = isoDark || isoLight;
  const isoLogoForDarkSurface = isoLight || isoDark;

  const fullLogo = resolvedSurfaceTone === 'dark'
    ? (fullLogoForDarkSurface || fullLogoForLightSurface)
    : (fullLogoForLightSurface || fullLogoForDarkSurface);
  const isoLogo = resolvedSurfaceTone === 'dark'
    ? (isoLogoForDarkSurface || isoLogoForLightSurface)
    : (isoLogoForLightSurface || isoLogoForDarkSurface);
  const hasOsSuffix = /\sOS$/i.test(studioName);

  return {
    studioName,
    fullLogo,
    isoLogo,
    fullLogoForLightSurface,
    fullLogoForDarkSurface,
    isoLogoForLightSurface,
    isoLogoForDarkSurface,
    surfaceTone: resolvedSurfaceTone,
    initials: studioInitials(studioName),
    brandMain: hasOsSuffix ? studioName.replace(/\sOS$/i, '') : studioName,
    brandAccent: hasOsSuffix ? 'OS' : '',
  };
}

export function BrandLogo({
  brand,
  variant = 'full',
  theme,
  surfaceTone,
  className,
  style,
  imgStyle,
  fallbackStyle,
  showText = true,
  ariaLabel,
} = {}) {
  const resolved = brand?.studioName
    ? brand
    : resolveStudioBrand({ data: brand?.data, studioLogo: brand?.studioLogo, studioBrandAssets: brand?.studioBrandAssets, theme, surfaceTone });
  const isIso = variant === 'iso' || variant === 'mark' || variant === 'compact';
  const resolvedSurfaceTone = surfaceTone || resolved.surfaceTone || (theme === 'dark' ? 'dark' : 'light');
  const logo = isIso
    ? (resolvedSurfaceTone === 'dark'
        ? (resolved.isoLogoForDarkSurface || resolved.isoLogo)
        : (resolved.isoLogoForLightSurface || resolved.isoLogo))
    : (resolvedSurfaceTone === 'dark'
        ? (resolved.fullLogoForDarkSurface || resolved.fullLogo)
        : (resolved.fullLogoForLightSurface || resolved.fullLogo));
  const label = ariaLabel || resolved.studioName || 'Feria OS';

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: isIso ? 'center' : 'flex-start',
    minWidth: 0,
    lineHeight: 1,
    ...style,
  };

  if (logo) {
    return (
      <span className={className} style={baseStyle} aria-label={label}>
        <img
          src={logo}
          alt={label}
          style={{
            display: 'block',
            width: isIso ? 34 : 'auto',
            height: isIso ? 34 : 32,
            maxWidth: isIso ? 34 : 190,
            objectFit: 'contain',
            borderRadius: isIso ? 10 : 0,
            ...imgStyle,
          }}
        />
      </span>
    );
  }

  if (isIso || !showText) {
    return (
      <span
        className={className}
        aria-label={label}
        style={{
          ...baseStyle,
          width: 34,
          height: 34,
          borderRadius: 11,
          background: 'var(--accent-faint, rgba(255,168,168,.11))',
          color: 'var(--accent, #ffa8a8)',
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: 0,
          ...fallbackStyle,
        }}
      >
        {resolved.initials || studioInitials(label)}
      </span>
    );
  }

  return (
    <span
      className={className || 'brand-mark'}
      aria-label={label}
      style={{
        ...baseStyle,
        color: 'var(--text, #F4F4F5)',
        fontSize: 21,
        fontWeight: 650,
        letterSpacing: 0,
        ...fallbackStyle,
      }}
    >
      {resolved.brandMain || label}
      {resolved.brandAccent && <> <em style={{ color: 'var(--accent, #ffa8a8)', fontStyle: 'italic', fontWeight: 600 }}>{resolved.brandAccent}</em></>}
    </span>
  );
}
